import React, { ChangeEvent, useState } from 'react';
import { Box, FormControl, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { CitizenLessonDeviationTitlezone } from './citizen-lesson-deviation-titlezone';
import { CitizenLessonDeviationLaunchbox } from './citizen-lesson-deviation-launchbox';
import { CitizenLessonDeviationRepetitions, CitizenLessonDeviationRepetitionRow } from './citizen-lesson-deviation-repetitions';
import { DatePickerValue } from '../../../../components/date-picker';
import { Accordion, AccordionSummary, AccordionDetails } from '../../../../components/accordion';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { ReadonlyInput } from '../../../../components/readonly-input';
import { TimePicker, TimePickerValue } from '../../../../components/time-picker';
import { RadioOption } from '../../../../components/radio-group';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import { ICitizenLessonDeviation, ICitizenLessonDeviationErrors } from '../../interfaces';
import { formatBoolean, formatDate, formatTimeHHMM } from '../../../../utilities';
import { TextField } from '../../../../components/text-field';
import { ConfirmDialog } from '../../../../components/confirm-dialog/confirm-dialog';

interface ICitizenLessonDeviationComponentProps {
  deviation: ICitizenLessonDeviation;
  errors: ICitizenLessonDeviationErrors;
  isEditMode: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  repetitionLessons: CitizenLessonDeviationRepetitionRow[];
  selectedRepetitionLessonIds: string[];
  deviationsToDeleteCount: number;
  currentLessonId?: string;

  onSaveClick: () => void;
  onDelete: () => void;
  onValidate: () => boolean;
  onYesNoChange: (name: string, value: YesNo) => void;
  onTimeChange: (name: string, value: TimePickerValue) => void;
  onCommentChange: (value?: string) => void;
  onRepetitionSelectionChange: (lessonIds: string[]) => void;
  onSelectSameTimeWindow: () => void;
  onSelectDeviationsInSameTimeWindow: () => void;
  onSelectByWeekdaysInPeriod: (weekdays: number[], fromDate: DatePickerValue, toDate: DatePickerValue) => void;
}

export function CitizenLessonDeviationComponent(props: ICitizenLessonDeviationComponentProps) {
  const {
    deviation,
    errors,
    isEditMode,
    isReadOnly,
    isSaving,
    repetitionLessons,
    selectedRepetitionLessonIds,
    deviationsToDeleteCount,
    currentLessonId,
    onSaveClick,
    onDelete,
    onValidate,
    onYesNoChange,
    onTimeChange,
    onCommentChange,
    onRepetitionSelectionChange,
    onSelectSameTimeWindow,
    onSelectDeviationsInSameTimeWindow,
    onSelectByWeekdaysInPeriod,
  } = props;

  const { courseName, citizenName, startDate, startTime, endTime, isExemptFromLesson, startCheck, endCheck, minTime, maxTime, comment } = deviation;
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  function handleSaveRequest() {
    setShowSaveDialog(true);
  }

  function closeSaveDialog() {
    setShowSaveDialog(false);
  }

  function handleConfirmSave() {
    closeSaveDialog();
    onSaveClick();
  }

  function handleYesNoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = (event.target as HTMLInputElement).value as YesNo;
    onYesNoChange(event.target.name, newValue);
  }

  function handleCommentChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    onCommentChange(event.currentTarget.value);
  }

  const saveDialogTitle = isEditMode ? 'Gem særplan' : 'Opret særplan';
  const saveDialogMessage = [
    isEditMode ? 'Ønsker du at gemme ændringerne til særplanen?' : 'Ønsker du at oprette særplanen?',
    selectedRepetitionLessonIds.length > 1 ? `Særplanen anvendes på ${selectedRepetitionLessonIds.length} lektioner.` : '',
  ]
    .filter(Boolean)
    .join('\r\n');

  return (
    <>
      <Uniform
        titleZone={<CitizenLessonDeviationTitlezone isEditMode={isEditMode} isReadOnly={isReadOnly} citizenName={citizenName} lessonDate={startDate} />}
        launchbox={
          <CitizenLessonDeviationLaunchbox
            isSaving={isSaving}
            onSave={handleSaveRequest}
            onValidate={onValidate}
            onDelete={onDelete}
            isEditMode={isEditMode}
            isReadOnly={isReadOnly}
            deviationsToDeleteCount={deviationsToDeleteCount}
          />
        }
        isEditMode={isEditMode}
        showButtons={!isEditMode && !isReadOnly}
        onOk={handleSaveRequest}
        onCancel={() => window.close()}
        okButtonIsLoading={isSaving}
        showSpinnerOnNextAndComplete={isSaving}
      >
        <UniformStep stepName="Generelt" validate={onValidate}>
          <UniformStepContainer>
            <Accordion defaultExpanded>
              <AccordionSummary>Om særplan</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="half">
                      <ReadonlyInput value={citizenName ?? ''} label="Borger" />
                    </Cell>
                    <Cell size="half">
                      <ReadonlyInput value={courseName ?? ''} label="Lektion" />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      {isReadOnly ? (
                        <ReadonlyInput label="Kommentar" value={comment} />
                      ) : (
                        <TextField label="Kommentar" variant="outlined" value={comment} onChange={handleCommentChange} />
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      {isReadOnly ? (
                        <ReadonlyInput label="Borger er fritaget for lektionen" value={formatBoolean(isExemptFromLesson)} />
                      ) : (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="exempt-radios" component="legend">
                            Borger er fritaget for lektionen
                          </FormLabel>
                          <RadioGroup aria-labelledby="exempt-radios" row name="isExemptFromLesson" value={isExemptFromLesson ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                            <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      {isReadOnly ? (
                        <ReadonlyInput label="Borger fremmødetjek ved lektions start" value={formatBoolean(startCheck)} />
                      ) : (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="start-check-radios" component="legend">
                            Borger fremmødetjek ved lektions start
                          </FormLabel>
                          <RadioGroup aria-labelledby="start-check-radios" row name="startCheck" value={startCheck ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                            <RadioOption value={YesNo.Yes} control={<Radio disabled={isExemptFromLesson} />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio disabled={isExemptFromLesson} />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      )}
                    </Cell>
                    <Cell size="half">
                      {isReadOnly ? (
                        <ReadonlyInput label="Borger fremmødetjek ved lektions afslutning" value={formatBoolean(endCheck)} />
                      ) : (
                        <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                          <FormLabel id="end-check-radios" component="legend">
                            Borger fremmødetjek ved lektions afslutning
                          </FormLabel>
                          <RadioGroup aria-labelledby="end-check-radios" row name="endCheck" value={endCheck ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                            <RadioOption value={YesNo.Yes} control={<Radio disabled={isExemptFromLesson} />} label="Ja" />
                            <RadioOption value={YesNo.No} control={<Radio disabled={isExemptFromLesson} />} label="Nej" />
                          </RadioGroup>
                        </FormControl>
                      )}
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      <ReadonlyInput label="Dato" value={formatDate(startDate)} />
                    </Cell>
                    <Cell size="quarters">
                      {isReadOnly ? (
                        <ReadonlyInput label="Starttidspunkt" value={formatTimeHHMM(startTime)} />
                      ) : (
                        <TimePicker
                          fullWidth
                          onChange={(value: TimePickerValue) => onTimeChange('startTime', value)}
                          value={startTime}
                          variant="outlined"
                          required
                          disabled={isExemptFromLesson}
                          label="Starttidspunkt"
                          error={!!errors.startTime}
                          errorText={errors.startTime}
                          minTime={minTime}
                          maxTime={maxTime}
                        />
                      )}
                    </Cell>
                    <Cell size="quarters">
                      {isReadOnly ? (
                        <ReadonlyInput label="Sluttidspunkt" value={formatTimeHHMM(endTime)} />
                      ) : (
                        <TimePicker
                          fullWidth
                          onChange={(value: TimePickerValue) => onTimeChange('endTime', value)}
                          value={endTime}
                          variant="outlined"
                          required
                          disabled={isExemptFromLesson}
                          label="Sluttidspunkt"
                          error={!!errors.endTime}
                          errorText={errors.endTime}
                          minTime={minTime}
                          maxTime={maxTime}
                        />
                      )}
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
            {!isReadOnly && repetitionLessons.length > 0 && (
              <Accordion>
                <AccordionSummary>Lektioner i fremmødeforløb</AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2">Vælg eventuelt flere lektioner, der skal omfattes af særplanen</Typography>
                  <Box mt={1}>
                    <CitizenLessonDeviationRepetitions
                      lessons={repetitionLessons}
                      selectedLessonIds={selectedRepetitionLessonIds}
                      currentLessonId={currentLessonId}
                      onSelectionChange={onRepetitionSelectionChange}
                      onSelectSameTimeWindow={onSelectSameTimeWindow}
                      onSelectDeviationsInSameTimeWindow={onSelectDeviationsInSameTimeWindow}
                      onApplyWeekdays={onSelectByWeekdaysInPeriod}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </UniformStepContainer>
        </UniformStep>
      </Uniform>
      <ConfirmDialog
        title={saveDialogTitle}
        message={saveDialogMessage}
        open={showSaveDialog}
        type="question"
        isLoading={isSaving}
        onConfirmClick={handleConfirmSave}
        onCancelClick={closeSaveDialog}
        confirmButtonText={isEditMode ? 'Gem' : 'Opret'}
        cancelButtonText="Annuller"
        onClose={closeSaveDialog}
      />
    </>
  );
}
